import { useQuery } from '@tanstack/react-query';
import {
  ASSETS_WITH_HEALTH_BY_ID,
  ASSET_MODELS,
  ASSET_SUMMARY_RERT_BY_ASSET,
  ASSET_TREE_BY_WORKSPACE,
  OVERVIEW_ASSETS,
  OVERVIEW_SUMMARY,
} from 'config/endpoints';
import { get } from 'utils/axios';
import { LOCAL_STORAGE_WORKSPACE_ID } from 'config/constants';
import {
  AssetTree,
  AssetModel,
  AssetSubItems,
  AssetDetails,
  Asset,
  AssetRertSummary,
} from './contract';
import { GenericQueryOptions } from './types';

export const useAssetsWithHealthQuery = (
  assetId?: string,
  queryOptions?: GenericQueryOptions<{ assets: Asset[] }>,
) => {
  return useQuery(
    ['ASSETS_WITH_HEALTH', assetId],
    get<{ assets: Asset[] }>(ASSETS_WITH_HEALTH_BY_ID(assetId)),
    { enabled: !!assetId, ...queryOptions },
  );
};

export const useAssetsByWorkspaceQuery = (
  assetCategories?: string[],
  queryOptions?: GenericQueryOptions<AssetTree>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;

  return useQuery(
    ['ASSET_ALL', 'OVERVIEW_LOADING', assetCategories],
    get<AssetTree>(ASSET_TREE_BY_WORKSPACE(workspaceId)),
    {
      ...queryOptions,
      enabled: workspaceId !== null,
    },
  );
};
export const useAssetsSummaryQuery = ({
  assetId,
  month,
  year,
  queryOptions,
}: {
  assetId?: string;
  month?: number | string;
  year?: number | string;
  queryOptions?: GenericQueryOptions<AssetRertSummary>;
}) =>
  useQuery(
    ['RERT', 'RERT_LOADING', 'ASSET_SUMMARY_RERT', assetId, month, year],
    async () =>
      get<AssetRertSummary, { field: string; include_descendants: boolean }>(
        ASSET_SUMMARY_RERT_BY_ASSET(assetId, month, year),
        {
          field: 'condition_evaluation',
          include_descendants: true,
        },
      )(),

    { enabled: !!assetId, ...queryOptions },
  );
export const useAssetsSubItemsQuery = ({
  assetId,

  category,
  queryOptions,
}: {
  assetId?: string;
  category?: string;
  queryOptions?: GenericQueryOptions<AssetSubItems>;
}) =>
  useQuery(
    ['OVERVIEW_LOADING', 'ASSET_SUBITEMS', assetId, category],
    async () => {
      const response = await get<AssetSubItems, { category?: string }>(OVERVIEW_ASSETS(assetId), {
        category,
      })();
      return response;
    },
    { enabled: !!assetId, retry: false, ...queryOptions },
  );
export const useAssetQuery = (
  assetId?: string,
  dateRange?: { startDate: string; endDate: string },
  queryOptions?: GenericQueryOptions<AssetDetails>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    [
      'OVERVIEW_LOADING',
      'ASSET',
      'PERMISSIONED',
      assetId,
      dateRange?.startDate,
      dateRange?.endDate,
    ],
    get<AssetDetails, { asset_id?: string }>(OVERVIEW_SUMMARY(workspaceId), {
      asset_id: assetId,
    }),

    { enabled: !!assetId && workspaceId !== null, retry: false, ...queryOptions },
  );
};

export const useAssetModelsQuery = (
  { assetId }: { assetId?: number | string },
  queryOptions?: GenericQueryOptions<AssetModel[]>,
) => useQuery(['ASSET_MODELS', assetId], get<AssetModel[]>(ASSET_MODELS(assetId)), queryOptions);
